// ────────────────────────────────────────────────────────────────────────────
// Clal "Apex" parser (אפקס חיים - בריאות / אפקס חיים)
//
// Proprietary fixed-width text format. Two variants:
//   health (אפקס חיים - בריאות) — Windows-1255, logical Hebrew, 6-digit SGB
//   life   (אפקס חיים)          — CP862, visual Hebrew, 4-digit SGB
//
// No official spec; offsets reverse-engineered from production samples.
//
// File roles:
//   .SGB — code→branch/coverage name lookup (short names)
//   .TRF — coverage CATALOG (9772 entries, richer names than SGB)
//   .SGP — code(2)→sub-type lookup (e.g. 01=רגילה, 02=ביטוח רפואי)
//   .MEV — insured persons (name, address, gender, birthdate)
//   .POL — policies (owner ת"ז, policy number)
//   .TAR — coverage/tariff lines (coverage codes, premiums per insured)
//   .HLV/.MAS/.MV2 — empty in samples — ignored
//
// Output: { members[], stats } — each member = one policy, matching the
// Harel health fund shape so FundCard/MasluliMTab auto-renders.
// ────────────────────────────────────────────────────────────────────────────

// windows-1255 (health) — natively supported by the browser's TextDecoder.
const dec1255 = (() => {
  try {
    const d = new TextDecoder("windows-1255");
    return (ab) => d.decode(ab instanceof ArrayBuffer ? new Uint8Array(ab) : ab);
  } catch {
    return (ab) => new TextDecoder("latin1").decode(ab instanceof ArrayBuffer ? new Uint8Array(ab) : ab);
  }
})();

// CP862 (IBM Hebrew DOS, VISUAL order) — used by the life (אפקס חיים) files.
// Browsers have no "cp862" TextDecoder, so we hand-roll it (same approach as
// harelParser): Hebrew letters live at 0x80+, digits/space/ASCII pass through.
const CP862_HEB = "אבגדהוזחטיךכלםמןנסעףפץצקרשת";
const CP862 = {};
for (let i = 0; i < CP862_HEB.length; i++) CP862[0x80 + i] = CP862_HEB[i];
function decCp862(arrayBuffer) {
  const bytes = new Uint8Array(arrayBuffer);
  let out = "";
  for (let i = 0; i < bytes.length; i++) {
    const b = bytes[i];
    if (b === 0x0d || b === 0x0a) out += String.fromCharCode(b);
    else if (b >= 0x20 && b < 0x80) out += String.fromCharCode(b); // ASCII (digits, space, +, ")
    else if (CP862[b]) out += CP862[b];
    else out += " ";
  }
  return out;
}

const VALID_TAZ = /^\d{9}$/;
const isStructuralBranch = (code) => code === "000001" || code === "000002"; // generic containers, not real coverages

// Reverse a visual-order Hebrew field to logical order (life/CP862 only),
// keeping Latin runs readable (same fix as harelParser.decodeHebField).
function reverseHeb(s) {
  const reversed = [...s.trim()].reverse().join("").trim();
  return reversed.replace(/[A-Za-z]+/g, (w) => [...w].reverse().join(""));
}

// ── per-variant layout config ────────────────────────────────────────────────
// health = אפקס חיים-בריאות (win-1255, logical order, 6-digit SGB codes)
// life   = אפקס חיים        (CP862, visual order, 4-digit SGB codes)
// Offsets reverse-engineered & calibrated against May-2026 samples vs the
// April-2026 Clal portal (life policy 855318 — sums/premiums/dates match to
// the agora). Amount convention (both variants): the field stores the ANNUAL
// premium in agorot (×100); monthly = field/1200, annual = field/100.
// sumInsured = field/100.
const VARIANTS = {
  health: {
    decode: (ab) => dec1255(ab),
    visualHeb: false,
    sgbWidth: 6,
    mev: { taz: [5, 14], name: [42, 54], street: [54, 66], city: [78, 90], gender: 117, birthDate: [111, 117] },
    pol: { taz: [22, 31], polNum: [366, 376], baseIndex: [315, 320], collectionDay: [320, 324] },
    // health coverage code is a 6-digit SGB code right-aligned in the record tail
    // (before a 2-digit sub-sequence), at a shifting position → tight tail-scan.
    // dates in the health layout are not reliably located → left null (best-effort).
    tar: { taz: [15, 24], polNum: [166, 176], covCode: null, tailScan: [176, 188], sum: [54, 64], premium: [64, 74], start: null, end: null, stop: null },
  },
  life: {
    decode: (ab) => decCp862(ab),
    visualHeb: true,
    sgbWidth: 4,
    mev: { taz: [5, 14], name: [36, 54], street: [54, 60], city: [60, 109], gender: 117, birthDate: [111, 117] },
    pol: { taz: [22, 31], polNum: [5, 12], baseIndex: [315, 320], collectionDay: [320, 324] },
    // life coverage code is at a FIXED position [24:28]; dates are YYMMDD.
    tar: { taz: [15, 24], polNum: [5, 13], covCode: [24, 28], scanFrom: () => 13, trfCode: null, sum: [54, 64], premium: [64, 73], start: [29, 35], end: [41, 47], stop: [47, 53] },
  },
};

// YYMMDD → DD/MM/YYYY (coverage dates are current/future → 20xx). Returns null if invalid.
function fmtYmd6(s) {
  if (!/^\d{6}$/.test(s)) return null;
  const yy = s.slice(0, 2), mm = +s.slice(2, 4), dd = +s.slice(4, 6);
  if (mm < 1 || mm > 12 || dd < 1 || dd > 31) return null;
  return `${s.slice(4, 6)}/${s.slice(2, 4)}/20${yy}`;
}

function toLines(arrayBuffer, V) {
  if (!arrayBuffer) return [];
  return V.decode(arrayBuffer).split(/\r?\n/).filter((l) => l.length > 0);
}

// ── lookups (code → name) ────────────────────────────────────────────────────
function buildLookup(lines, codeWidth, visualHeb) {
  const map = new Map();
  for (const l of lines) {
    if (l.length < codeWidth) continue;
    const code = l.slice(0, codeWidth);
    let name = l.slice(codeWidth);
    name = visualHeb ? reverseHeb(name) : name.trim();
    // life SGB names carry a stray "000" prefix/suffix from the fixed field — strip it.
    if (visualHeb) name = name.replace(/^0{2,}\s*/, "").replace(/\s*0{2,}$/, "").trim();
    if (code.trim()) map.set(code, name);
  }
  return map;
}

const text = (l, [a, b], visualHeb) => {
  const raw = l.slice(a, b);
  return visualHeb ? reverseHeb(raw) : raw.trim();
};

// ── TRF coverage catalog ────────────────────────────────────────────────────
// Each line: [4-char prefix][coverage name][zero-padding][code digits]
// Returns Map<code, name> with richer names than SGB (e.g. "הגנה עצמית - פלוס").
function buildTrfCatalog(lines, V) {
  const map = new Map();
  for (const l of lines) {
    if (l.length < 8) continue;
    const m = l.match(/(\d+)\s*$/);
    if (!m) continue;
    const code = m[1].replace(/^0+/, "") || "0";
    if (code === "0") continue;
    let name = l.slice(4, m.index).replace(/0+$/, "").trim();
    if (V.visualHeb) name = reverseHeb(name);
    if (!name) continue;
    map.set(code, name);
  }
  return map;
}

// ── insured persons (.MEV) ───────────────────────────────────────────────────
function parseMev(lines, V) {
  const byTaz = new Map();
  for (const l of lines) {
    const taz = l.slice(...V.mev.taz);
    if (!VALID_TAZ.test(taz)) continue;
    const name = text(l, V.mev.name, V.visualHeb);
    const street = text(l, V.mev.street, V.visualHeb);
    // city field can carry adjacent numerics (phone/zip) in the life layout —
    // keep only Hebrew/Latin words.
    const city = text(l, V.mev.city, V.visualHeb).replace(/[0-9+"'\-]/g, " ").replace(/\s+/g, " ").trim();
    const genderCh = l[V.mev.gender] || "";
    const gender = genderCh === "ז" ? "ז" : genderCh === "נ" ? "נ" : "";
    let birthDate = null;
    if (V.mev.birthDate) {
      const bd = l.slice(...V.mev.birthDate);
      if (/^\d{6}$/.test(bd)) {
        const yy = parseInt(bd.slice(0, 2));
        const mm = bd.slice(2, 4);
        const dd = bd.slice(4, 6);
        const year = yy > 50 ? 1900 + yy : 2000 + yy;
        birthDate = `${dd}/${mm}/${year}`;
      }
    }
    if (!byTaz.has(taz)) byTaz.set(taz, { taz, name, street, city, gender, birthDate });
  }
  return byTaz;
}

// ── policies (.POL) ──────────────────────────────────────────────────────────
function parsePol(lines, V) {
  const policies = [];
  for (const l of lines) {
    const ownerTaz = l.slice(...V.pol.taz);
    const polNum = l.slice(...V.pol.polNum).trim();
    if (!VALID_TAZ.test(ownerTaz) || !polNum) continue;
    // policy-level summary fields (best-effort; validated ranges)
    let baseIndex = null, collectionDay = null;
    if (V.pol.baseIndex) {
      const bi = parseInt(l.slice(...V.pol.baseIndex), 10);
      if (!isNaN(bi) && bi > 1000 && bi < 100000) baseIndex = bi;
    }
    if (V.pol.collectionDay) {
      const cd = parseInt(l.slice(...V.pol.collectionDay), 10);
      if (!isNaN(cd) && cd >= 1 && cd <= 31) collectionDay = cd;
    }
    policies.push({ polNum, ownerTaz, baseIndex, collectionDay });
  }
  return policies;
}

// ── coverage lines (.TAR) ────────────────────────────────────────────────────
// Derive a coarse "coverage type" (סוג כיסוי) from the resolved name / amounts.
function coverageType(name, kind, sumInsured) {
  if (name && /שחרור/.test(name)) return "שחרור";
  if (kind === "life") return "ביטוח חיים";
  return "בריאות";
}

function parseTar(lines, sgb, trf, V, kind) {
  const rows = [];
  for (const l of lines) {
    const taz = l.slice(...V.tar.taz);
    const polNum = l.slice(...V.tar.polNum).trim();
    if (!VALID_TAZ.test(taz) || !polNum) continue;

    // ── coverage code + name ──
    let code = null, name = null;
    if (V.tar.covCode && l.length >= V.tar.covCode[1]) {
      // life: code at a fixed position; resolve via SGB then TRF.
      code = l.slice(...V.tar.covCode).replace(/^0+/, "") || l.slice(...V.tar.covCode).trim();
      const padded = code.padStart(V.sgbWidth, "0");
      name = sgb.get(padded) || sgb.get(code) || trf.get(code) || null;
    } else if (V.tar.tailScan) {
      // health: scan the record tail for an SGB-resolvable, non-structural code.
      const [s0, s1] = V.tar.tailScan;
      const w = V.sgbWidth;
      for (let c = s0; c + w <= Math.min(s1, l.length); c++) {
        const cand = l.slice(c, c + w);
        if (!/^\d+$/.test(cand) || isStructuralBranch(cand) || !sgb.has(cand)) continue;
        code = cand.replace(/^0+/, "") || cand;
        name = sgb.get(cand);
        break;
      }
      if (!code) {
        // no SGB match — use the 6-digit window before the 2-digit sub-sequence.
        const raw = l.slice(s0 + 4, s0 + 10);
        code = /^\d+$/.test(raw) ? (raw.replace(/^0+/, "") || raw) : null;
      }
    }
    if (code && isStructuralBranch(code.padStart(6, "0"))) continue; // skip generic containers

    // ── amounts (field = annual premium ×100 in agorot; sum = ×100) ──
    const num = (range) => {
      if (!range || l.length < range[1]) return null;
      const v = parseInt(l.slice(...range), 10);
      return isNaN(v) ? null : v;
    };
    const sumRaw = num(V.tar.sum);
    const premRaw = num(V.tar.premium);
    const sumInsured = sumRaw ? sumRaw / 100 : null;
    const annualPremium = premRaw ? premRaw / 100 : null;
    const monthlyPremium = premRaw ? premRaw / 1200 : null;

    // ── dates (YYMMDD; life only) ──
    const startDate = V.tar.start ? fmtYmd6(l.slice(...V.tar.start)) : null;
    const endDate = V.tar.end ? fmtYmd6(l.slice(...V.tar.end)) : null;
    const paymentStopDate = V.tar.stop ? fmtYmd6(l.slice(...V.tar.stop)) : null;

    rows.push({
      taz, polNum,
      coverageCode: code || null,
      coverageName: name,
      coverageType: coverageType(name, kind, sumInsured),
      sumInsured, annualPremium, monthlyPremium,
      startDate, endDate, paymentStopDate,
    });
  }
  return rows;
}

// ── assembly ─────────────────────────────────────────────────────────────────
// Produce one member per policy. Each member carries the policy owner's info
// and funds[] = coverage entries (matching the Harel health fund shape so
// FundCard / MasluliMTab auto-renders).
export function parseClalApex(filesByExt, meta = {}) {
  const kind = meta.kind === "life" ? "life" : "health";
  try {
  const V = VARIANTS[kind];
  if (!V) throw new Error(`unsupported Apex kind: ${kind}`);
  const sgb = buildLookup(toLines(filesByExt?.SGB, V), V.sgbWidth, V.visualHeb);
  const trf = buildTrfCatalog(toLines(filesByExt?.TRF, V), V);
  const insured = parseMev(toLines(filesByExt?.MEV, V), V);
  const policies = parsePol(toLines(filesByExt?.POL, V), V);
  const tarRows = parseTar(toLines(filesByExt?.TAR, V), sgb, trf, V, kind);

  const normPol = (p) => String(p || "").replace(/^0+/, "") || "0";

  // Group TAR rows by normalized policy number
  const polCoverages = new Map();
  for (const r of tarRows) {
    const key = normPol(r.polNum);
    if (!polCoverages.has(key)) polCoverages.set(key, []);
    polCoverages.get(key).push(r);
  }

  // Deduplicate POL records by normalized polNum
  const seenPol = new Map();
  for (const p of policies) {
    const key = normPol(p.polNum);
    if (!seenPol.has(key)) seenPol.set(key, p);
  }

  const nameOf = (taz) => insured.get(taz)?.name || "";

  const members = [];
  for (const [, p] of seenPol) {
    const owner = insured.get(p.ownerTaz);
    const coverages = polCoverages.get(normPol(p.polNum)) || [];

    // Collapse only truly-identical rows (same code, insured, amounts, dates);
    // distinct financial lines that share a code are kept.
    const covDedup = new Map();
    for (const c of coverages) {
      const dk = `${c.coverageCode || ""}_${c.taz}_${c.monthlyPremium || 0}_${c.sumInsured || 0}_${c.startDate || ""}_${c.endDate || ""}`;
      if (!covDedup.has(dk)) covDedup.set(dk, c);
    }

    const funds = [...covDedup.values()].map((c) => ({
      name: c.coverageName || (c.coverageCode ? `כיסוי ${c.coverageCode}` : "כיסוי"),
      coverageType: c.coverageType || null,
      productCode: c.coverageCode || "",
      monthlyPremium: c.monthlyPremium ?? null,
      annualPremium: c.annualPremium ?? null,
      sumInsured: c.sumInsured ?? null,
      startDate: c.startDate || null,
      endDate: c.endDate || null,
      paymentStopDate: c.paymentStopDate || null,
      duration: null,
      insuredTaz: c.taz,
      insuredName: nameOf(c.taz),
      insuredRole: c.taz === p.ownerTaz ? "ראשי" : "משני",
    }));

    members.push({
      rawId: p.polNum,
      taz: p.ownerTaz,
      firstName: owner?.name || "",
      lastName: "",
      city: owner?.city || "",
      gender: owner?.gender || "",
      birthDate: owner?.birthDate || null,
      street: owner?.street || "",
      productType: kind,
      baseIndex: p.baseIndex ?? null,
      collectionDay: p.collectionDay ?? null,
      funds,
    });
  }

  return {
    members,
    stats: {
      insured: insured.size,
      policies: seenPol.size,
      coverageLines: tarRows.length,
      members: members.length,
      trfEntries: trf.size,
    },
    meta,
  };
  } catch (e) {
    // פרסר Apex של כלל ניסיוני (v2) — וריאנט קובץ לא נתמך לא יפיל את הייבוא;
    // מחזירים תוצאה ריקה בטוחה במקום לזרוק.
    // eslint-disable-next-line no-console
    console.warn("[clalApex] parse failed, returning empty:", e.message);
    return {
      members: [],
      stats: { insured: 0, policies: 0, coverageLines: 0, members: 0, trfEntries: 0 },
      meta, _error: e.message,
    };
  }
}
